import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { useToast } from '@/hooks/useToast';
import { formatFileSize, isImage } from '@/lib/files';
import type { ReceiptAttachment } from '@/lib/types';
import { IconAlert, IconDownload, IconExternal, IconFileText, IconTrash } from '@/components/Icons';

const fileKey = (receiptId: string, attachmentId: string) => ['attachment', receiptId, attachmentId] as const;
const filePath = (receiptId: string, attachmentId: string) => `/api/receipts/${receiptId}/attachments/${attachmentId}`;

/** Quanto a URL local vive depois de aberta em outra aba ou baixada. */
const RELEASE_AFTER_MS = 60_000;

interface AttachmentTileProps {
  receiptId: string;
  attachment: ReceiptAttachment;
  removable: boolean;
  busy: boolean;
  onRemove: () => void;
}

/**
 * Um arquivo do comprovante. Imagens aparecem na hora; PDFs só são baixados
 * quando alguém pede para abrir ou baixar.
 */
export function AttachmentTile({ receiptId, attachment, removable, busy, onRemove }: AttachmentTileProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const image = isImage(attachment.mimeType);

  const { data: blob, isError } = useQuery({
    queryKey: fileKey(receiptId, attachment.id),
    queryFn: () => api.blob(filePath(receiptId, attachment.id)),
    enabled: image,
    staleTime: Infinity,
  });
  const preview = useObjectUrl(blob);

  async function withUrl(action: (url: string) => void) {
    const file = await queryClient.fetchQuery({
      queryKey: fileKey(receiptId, attachment.id),
      queryFn: () => api.blob(filePath(receiptId, attachment.id)),
      staleTime: Infinity,
    });
    const url = URL.createObjectURL(file);
    action(url);
    setTimeout(() => URL.revokeObjectURL(url), RELEASE_AFTER_MS);
  }

  function open() {
    // A aba abre ainda no clique — depois de esperar o download, o bloqueador
    // de pop-up barraria. O arquivo entra nela quando chegar.
    const tab = window.open('', '_blank');
    // A aba nova não precisa — nem deve — conseguir alcançar esta página de volta.
    if (tab) tab.opener = null;
    withUrl((url) => {
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    }).catch(() => {
      tab?.close();
      toast.error('Não foi possível abrir o arquivo. Tente de novo.');
    });
  }

  function download() {
    withUrl((url) => {
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      document.body.append(link);
      link.click();
      link.remove();
    }).catch(() => toast.error('Não foi possível baixar o arquivo. Tente de novo.'));
  }

  return (
    <div className="file-tile">
      <div className="file-thumb">
        {image && preview ? (
          <img src={preview} alt={`Comprovante: ${attachment.fileName}`} />
        ) : image && isError ? (
          <IconAlert size={20} />
        ) : image ? (
          <span className="spinner" />
        ) : (
          <IconFileText size={26} />
        )}
      </div>
      <div className="file-meta">
        <span className="truncate" title={attachment.fileName}>{attachment.fileName}</span>
        <span className="muted num">{formatFileSize(attachment.size)}</span>
      </div>
      <div className="file-actions">
        <button type="button" className="btn btn-sm btn-ghost" onClick={open}>
          <IconExternal /> Abrir
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={download} aria-label={`Baixar ${attachment.fileName}`}>
          <IconDownload />
        </button>
      </div>
      <button
        type="button"
        className="icon-btn file-remove"
        disabled={!removable || busy}
        title={removable ? 'Remover arquivo' : 'O comprovante precisa de ao menos um arquivo'}
        aria-label={`Remover ${attachment.fileName}`}
        onClick={onRemove}
      >
        <IconTrash />
      </button>
    </div>
  );
}
